import React from 'react';
import { Heart, Check, Crown, Flame } from 'lucide-react';
import type { BattlePlayerState } from '../../../types/battleRush';

interface BattlePlayerListProps {
  players: BattlePlayerState[];
  currentUserId: string | null;
  hostUserId?: string;
  showAnswered?: boolean;
  showReady?: boolean;
  compact?: boolean;
}

export const BattlePlayerList: React.FC<BattlePlayerListProps> = ({
  players,
  currentUserId,
  hostUserId,
  showAnswered = false,
  showReady = false,
  compact = false,
}) => {
  return (
    <div className={`flex flex-col ${compact ? 'gap-1.5' : 'gap-2'}`}>
      {players.map((player) => {
        const isMe = player.userId === currentUserId;
        const isHost = hostUserId != null && player.userId === hostUserId;
        const opacity = player.isEliminated ? 0.4 : 1;

        return (
          <div
            key={player.userId}
            className={`flex items-center justify-between rounded-lg px-3 ${compact ? 'py-1.5' : 'py-2'}`}
            style={{
              opacity,
              backgroundColor: isMe ? 'rgba(139, 92, 246, 0.15)' : 'rgba(255, 255, 255, 0.05)',
              border: isMe ? '1px solid rgba(139, 92, 246, 0.3)' : '1px solid transparent',
            }}
          >
            <div className="flex items-center gap-2 min-w-0">
              {showReady && isHost && (
                <Crown className="w-4 h-4 flex-shrink-0" style={{ color: '#fbbf24' }} />
              )}
              {showReady && !isHost && (
                <div
                  className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center"
                  style={{
                    backgroundColor: player.isReady ? 'var(--rush-correct)' : 'rgba(255, 255, 255, 0.1)',
                    border: player.isReady ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  {player.isReady && <Check className="w-3 h-3" style={{ color: '#fff' }} />}
                </div>
              )}
              <span
                className={`font-medium truncate ${compact ? 'text-sm' : 'text-base'}`}
                style={{ color: 'var(--rush-text-primary)' }}
              >
                {player.username}
                {isMe && <span className="text-xs ml-1 opacity-60">(you)</span>}
              </span>
              {showAnswered && player.hasAnswered && !player.isEliminated && (
                <Check className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--rush-correct)' }} />
              )}
            </div>

            <div className="flex items-center gap-3 flex-shrink-0">
              {/* Lives */}
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Heart
                    key={i}
                    className={`w-3.5 h-3.5 ${i < player.lives ? '' : 'opacity-20'}`}
                    fill={i < player.lives ? 'var(--rush-wrong)' : 'none'}
                    stroke={i < player.lives ? 'var(--rush-wrong)' : 'var(--rush-text-muted)'}
                  />
                ))}
              </div>

              {/* Score */}
              <div className="flex items-center gap-0.5">
                <Flame className="w-3.5 h-3.5" fill="#f59e0b" stroke="#f59e0b" />
                <span
                  className="text-sm font-mono font-bold min-w-[2ch] text-right"
                  style={{ color: 'var(--rush-text-primary)' }}
                >
                  {player.score}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
